import { httpClient, getAuthToken, API_BASE_URL } from './core';

export interface AiSettingsDto {
  enabled: boolean;
  model: string;
  systemPrompt: string;
  guestSystemPrompt: string;
  authenticatedSystemPrompt: string;
  guestGreeting?: string;
  authenticatedGreeting?: string;
  chatLoggingEnabled?: boolean;
  chatRetentionDays?: number;
  chatPseudonymizationMode?: 'delete' | 'mask';
  /** Nur noch für ältere Antworten; der Schlüssel kommt aus der Serverkonfiguration. */
  apiKey?: string;
  apiKeyIsSet?: boolean;
  availableModels?: string[];
  availableChatPseudonymizationModes?: Array<'delete' | 'mask'>;
}

export interface AiAgentToolCall {
  name: string;
  arguments: Record<string, unknown>;
}

export interface AiAgentChartSeries {
  name: string;
  values: number[];
}

export interface AiAgentChart {
  type: 'line' | 'bar';
  title: string;
  xLabel: string;
  yLabel: string;
  labels: string[];
  series: AiAgentChartSeries[];
}

export interface AiAgentStructuredBlocks {
  kpis: Array<{ label: string; value: number; tool?: string }>;
  insights: string[];
  warnings: string[];
  actions: string[];
  followups: string[];
  confidence: number;
  explanation: {
    usedTools: string[];
    appliedFilters: Record<string, unknown>;
    assumptions: string[];
  };
  blocks: Array<Record<string, unknown>>;
}

export interface AiAgentResponse {
  sessionId: string;
  mode: 'chat' | 'data';
  answer: string;
  answerMarkdown?: string;
  structuredBlocks?: AiAgentStructuredBlocks;
  chainMeta?: { iterations: number; executed: number; stoppedBy: string | null };
  requiresConfirmation: boolean;
  pendingActionToken: string | null;
  proposedAction: { name: string; arguments: Record<string, unknown>; label?: string } | null;
  toolCalls: AiAgentToolCall[];
  sources: string[];
  chart: AiAgentChart | null;
  /** Nach „Zahlen als CSV“ (T-0198): Datei zum Speichern im Browser. */
  download?: { fileName: string; mimeType: string; base64: string };
}

/** Gespeicherte Antwort des Assistenten (T-0198). */
export interface KiAuswertung {
  id: number;
  name: string;
  frage: string;
  antwort: string;
  createdAt: string;
}

export interface AiAgentQueryPayload {
  message: string;
  sessionId?: string;
  mode?: 'chat' | 'data';
  historyLimit?: number;
  comparisonSessionId?: string;
  drilldownContext?: { label: string; series: string; value: number };
}

export interface AiAgentConfirmPayload {
  sessionId: string;
  pendingActionToken: string;
  confirm: boolean;
}

export const aiSettingsApi = {
  get: async (): Promise<AiSettingsDto> => {
    const res = await httpClient<AiSettingsDto>('/api/settings/ai');
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der KI-Einstellungen');
    return res.data;
  },
  update: async (payload: AiSettingsDto): Promise<AiSettingsDto> => {
    const res = await httpClient<AiSettingsDto>('/api/settings/ai', {
      method: 'PUT',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Speichern der KI-Einstellungen');
    return res.data;
  },
};

export const kiAuswertungenApi = {
  list: async (): Promise<KiAuswertung[]> => {
    const res = await httpClient<{ items: KiAuswertung[] }>('/api/ai/auswertungen');
    if (res.error || !res.data) throw new Error(res.error || 'Gespeicherte Auswertungen konnten nicht geladen werden');
    return res.data.items || [];
  },
  remove: async (id: number): Promise<void> => {
    const res = await httpClient(`/api/ai/auswertungen/${id}`, { method: 'DELETE' } as RequestInit);
    if (res.error) throw new Error(res.error || 'Löschen fehlgeschlagen');
  },
};

export const aiAgentApi = {
  query: async (payload: AiAgentQueryPayload): Promise<AiAgentResponse> => {
    const res = await httpClient<AiAgentResponse>('/api/ai/agent/query', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler bei KI-Anfrage');
    return res.data;
  },
  queryStream: async (
    payload: AiAgentQueryPayload,
    onChunk: (chunk: { type: string; stage?: string; message?: string; data?: AiAgentResponse }) => void
  ): Promise<void> => {
    const token = getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const fullUrl = `${API_BASE_URL}/api/ai/agent/query-stream`;
    const response = await fetch(fullUrl, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok || !response.body) {
      throw new Error(`Fehler bei KI-Streaming (HTTP ${response.status})`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let done = false;
    while (!done) {
      const chunkResult = await reader.read();
      done = chunkResult.done;
      const value = chunkResult.value;
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() || '';
      for (const chunk of chunks) {
        const line = chunk.split('\n').find((l) => l.startsWith('data: '));
        if (!line) continue;
        try {
          const data = JSON.parse(line.slice(6));
          onChunk(data);
        } catch {
          // ignore malformed chunk
        }
      }
    }
  },
  confirm: async (payload: AiAgentConfirmPayload): Promise<AiAgentResponse> => {
    const res = await httpClient<AiAgentResponse>('/api/ai/agent/confirm', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler bei KI-Bestaetigung');
    return res.data;
  },
};

export interface ChatConversationSessionDto {
  id: number;
  sessionId: string;
  source: string;
  mode: 'guest' | 'authenticated';
  wpUserId: number | null;
  standortId: number | null;
  messageCount: number;
  firstMessageAt: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
  pseudonymizedAt: string | null;
  lastMessagePreview?: string;
}

export interface ChatConversationMessageDto {
  id: number;
  chatSessionId: number;
  direction: 'user' | 'assistant' | 'system';
  content: string;
  contentMasked: string | null;
  intent: string | null;
  piiFlag: boolean;
  createdAt: string;
}

export interface ChatConversationsListResponse {
  items: ChatConversationSessionDto[];
  pagination: { page: number; pageSize: number; total: number };
}

export interface ChatConversationDetailResponse {
  session: ChatConversationSessionDto;
  messages: ChatConversationMessageDto[];
}

export const chatConversationsApi = {
  list: async (params: {
    from?: string;
    to?: string;
    mode?: 'guest' | 'authenticated';
    standortId?: number;
    intent?: string;
    query?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ChatConversationsListResponse> => {
    const search = new URLSearchParams();
    if (params.from) search.set('from', params.from);
    if (params.to) search.set('to', params.to);
    if (params.mode) search.set('mode', params.mode);
    if (params.standortId && params.standortId > 0) search.set('standortId', String(params.standortId));
    if (params.intent) search.set('intent', params.intent);
    if (params.query) search.set('query', params.query);
    search.set('page', String(params.page ?? 1));
    search.set('pageSize', String(params.pageSize ?? 20));
    const res = await httpClient<ChatConversationsListResponse>(`/api/chat-conversations?${search.toString()}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Chat-Konversationen');
    return res.data;
  },
  get: async (sessionId: string): Promise<ChatConversationDetailResponse> => {
    const res = await httpClient<ChatConversationDetailResponse>(`/api/chat-conversations/${encodeURIComponent(sessionId)}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Chat-Konversation');
    return res.data;
  },
};
